import { orgAccessPatch } from '../../utils/newHackathon';
import dayjs from 'dayjs';
import { HelpLink } from '../HelpLink';
import React, { useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useDesktopMode } from '../../services/hooks/useDesktopMode';
import { DatePicker } from '@mui/x-date-pickers';
import Editor from '../Editor/Editor';

import TuneIcon from '@mui/icons-material/Tune';
import EventIcon from '@mui/icons-material/Event';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import GavelIcon from '@mui/icons-material/Gavel';
import PaletteIcon from '@mui/icons-material/Palette';
import ArticleIcon from '@mui/icons-material/Article';
import { PageContainer } from '../PageContainer';
import { CategoryEditor } from './CategoryEditor';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import TextField from '@mui/material/TextField/TextField';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Checkbox from '@mui/material/Checkbox/Checkbox';
import FormControl from '@mui/material/FormControl/FormControl';
import FormLabel from '@mui/material/FormLabel/FormLabel';
import Slider from '@mui/material/Slider/Slider';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Button from '@mui/material/Button/Button';
import Typography from '@mui/material/Typography/Typography';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Alert from '@mui/material/Alert/Alert';
import { Branding } from '../../services/branding.service';
import { OrganizationSummary } from '../../services/organization.service';
import { useMyOrganizations } from '../../hooks/useMyOrganizations';
import { BrandingEditor } from '../Branding/BrandingEditor';
import { formatPrice, getPricing, Pricing } from '../../services/billing.service';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const preferredAuthOptions = ['google'];

export interface EditHackathonData {
    name: string;
    description: string;
    startDate: dayjs.Dayjs | null;
    endDate: dayjs.Dayjs | null;
    ideaSubmissionDeadline: dayjs.Dayjs | null;
    ideaTemplate: string;
    categories: string[];
    // categoryMeta[i] describes categories[i]; _id is the category's permanent id
    categoryMeta: Array<{ _id?: string, judgeOnly: boolean, description: string }>;
    inviteCode: string;
    domain: string;
    preferredAuth: string;
    minTeamSize: number;
    maxTeamSize: number;
    blindJudgeScores: boolean;
    anonymousJudgeScores: boolean;
    participantScores: boolean;
    branding: Branding;
    // organization id, '' for none
    organization: string;
}

type LinkedOrganization = Pick<OrganizationSummary, '_id' | 'name' | 'slug' | 'branding'>;

const EditorSection = ({
    icon,
    title,
    description,
    help,
    children
}: {
    icon: React.ReactNode,
    title: string,
    description?: string,
    // a HelpLink to the guide for this section
    help?: React.ReactNode,
    children: React.ReactNode
}) => {
    return (
        <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2.5}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ color: 'primary.main', display: 'flex', pt: 0.25 }}>{icon}</Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h4" component="h2">{title}</Typography>
                            {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
                        </Box>
                        {help}
                    </Stack>
                    {children}
                </Stack>
            </CardContent>
        </Card>
    )
}

const SettingToggle = ({
    checked,
    label,
    description,
    onChange
}: {
    checked: boolean,
    label: string,
    description: string,
    onChange: (checked: boolean) => void
}) => {
    return (
        <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={<Checkbox sx={{ pt: 0.25 }} checked={checked} onChange={(e) => onChange(e.target.checked)} />}
            label={
                <Box>
                    <Typography variant="subtitle2">{label}</Typography>
                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                </Box>
            } />
    )
}

// a date is a whole day: it has passed once that day is over (run026: an event created just after midnight with
// yesterday's date ended on creation and locked its teams, with nothing on the form saying so)
const passed = (date: dayjs.Dayjs | null) => !!date && date.endOf('day').isBefore(dayjs());

export const HackathonEditor = ({
    data,
    setData,
    onSubmit,
    headingLabel,
    submitLabel,
    linkedOrganization,
    submitError,
    plan,
    upgradeUrl,
    intro,
    applyOrgAccess,
    actions
}: {
    data: EditHackathonData, // init data only
    setData: React.Dispatch<React.SetStateAction<EditHackathonData>>,
    onSubmit: () => void,
    headingLabel: string,
    submitLabel: string,
    // the hackathon's current organization, which may not be one the user belongs to
    linkedOrganization?: LinkedOrganization | null,
    submitError?: string,
    // the event's plan when editing (undefined while creating): branding shows to participants only on Pro
    plan?: 'free' | 'pro',
    // where an organizer upgrades this event
    upgradeUrl?: string,
    // shown under the heading, e.g. "copy settings from a past hackathon" when creating
    intro?: React.ReactNode,
    // creating: picking an organization brings its access rule (who can join) into Access
    applyOrgAccess?: boolean,
    // more buttons beside the submit button, e.g. Preview
    actions?: React.ReactNode
}) => {

    const desktopMode = useDesktopMode();

    // branding is a Pro event feature: show its price next to the branding settings
    const [pricing, setPricing] = React.useState<Pricing | null>(null);
    React.useEffect(() => {
        if (plan !== 'pro') {
            getPricing().then(setPricing, () => setPricing(null));
        }
    }, [plan]);
    const proPrice = pricing ? formatPrice(pricing.proEventPrice, pricing.currency) : '';

    // links like "Set up branding" land on the branding section (the router doesn't scroll to hashes)
    const location = useLocation();
    React.useEffect(() => {
        if (location.hash === '#branding') {
            window.setTimeout(() => document.getElementById('branding')?.scrollIntoView({ behavior: 'smooth' }), 300);
        }
    }, [location.hash]);

    const { organizations } = useMyOrganizations();

    const organizationOptions = useMemo(() => {
        const options: LinkedOrganization[] = [...organizations];
        if (linkedOrganization && !options.some(o => o._id === linkedOrganization._id)) {
            options.push(linkedOrganization);
        }
        return options;
    }, [organizations, linkedOrganization]);

    const selectedOrganization = organizationOptions.find(o => o._id === data.organization);
    // the selected organization's access rule, when it's one of the user's (it carries one)
    const orgAccess = organizations.find(o => o._id === data.organization)?.access;

    // functional updates: these fire after a debounce, so they must not overwrite fields edited since
    const setDescription = useCallback((description: string) => setData(d => ({ ...d, description })), [setData]);
    const setIdeaTemplate = useCallback((ideaTemplate: string) => setData(d => ({ ...d, ideaTemplate })), [setData]);
    const setBranding = useCallback((branding: Branding) => setData(d => ({ ...d, branding })), [setData]);

    // the branding preview shows the name; let it lag a keystroke behind instead of slowing typing
    const previewName = useDeferredValue(data.name);


    const [error, setError] = useState<string>('');

    return (
        <>
            <PageContainer variant="plain">
                <Stack spacing={3} sx={{ maxWidth: 960, mx: 'auto' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                        <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>{headingLabel}</Typography>
                        <HelpLink guide="create">Help with this form</HelpLink>
                    </Stack>
                    {intro}

                    <EditorSection icon={<TuneIcon />} title="Basics" description="What participants see first.">
                        <TextField label="Name" value={data.name} onChange={e => setData({ ...data, name: e.target.value })} />
                        <React.Suspense fallback={<div>Loading...</div>}>
                            <Editor toolbar={true} value={data.description} onChange={setDescription} />
                        </React.Suspense>
                    </EditorSection>

                    <EditorSection icon={<EventIcon />} title="Dates">
                        <Stack direction={desktopMode ? 'row' : 'column'} spacing={2}>
                            <DatePicker label="Idea Submission Deadline" value={data.ideaSubmissionDeadline} onChange={date => setData({ ...data, ideaSubmissionDeadline: date })}
                                slotProps={{ textField: passed(data.ideaSubmissionDeadline) ? { error: true, helperText: 'Already passed: ideas are closed' } : {} }} />
                            <DatePicker label="Start Date" value={data.startDate} onChange={date => setData({ ...data, startDate: date })} />
                            <DatePicker label="End Date" value={data.endDate} onChange={date => setData({ ...data, endDate: date })}
                                slotProps={{ textField: passed(data.endDate) ? { error: true, helperText: 'Already passed: the event shows as ended and teams are locked' } : {} }} />
                        </Stack>
                    </EditorSection>

                    <EditorSection icon={<GroupsIcon />} title="Teams">
                        <FormControl>
                            <FormLabel>Team size: {data.minTeamSize === data.maxTeamSize ? data.maxTeamSize : `${data.minTeamSize}-${data.maxTeamSize}`} people</FormLabel>

                            <Slider sx={{ width: 260, ml: 1 }} value={[data.minTeamSize, data.maxTeamSize]} onChange={(e, value) => {
                                const vals = value as number[];
                                const min = vals[0] || 1;
                                const max = vals[1] || 10;
                                setData({ ...data, minTeamSize: min, maxTeamSize: max })
                            }} valueLabelDisplay="auto" min={1} max={10} marks
                                // two thumbs on one track: name each, so screen readers (and keyboard users) know which they're moving
                                getAriaLabel={(index) => index === 0 ? 'Smallest team size' : 'Largest team size'}
                                getAriaValueText={(value) => `${value} ${value === 1 ? 'person' : 'people'}`} />
                        </FormControl>
                    </EditorSection>

                    <EditorSection icon={<LockIcon />} title="Access" description="Control who can join.">
                        <Stack direction={desktopMode ? 'row' : 'column'} spacing={2}>
                            <TextField label="Invite Code" value={data.inviteCode}
                                helperText="Makes the hackathon invite-only"
                                onChange={e => setData({ ...data, inviteCode: e.target.value })} />
                            <TextField label="Email domain" value={data.domain} placeholder="example.com"
                                helperText={orgAccess?.domain && data.domain === orgAccess.domain
                                    ? `${selectedOrganization?.name}'s standard: only @${orgAccess.domain} emails can join`
                                    : 'Only people with this email domain can join'}
                                onChange={e => setData({ ...data, domain: e.target.value })} />
                            <TextField select label="Preferred sign-in" sx={{ minWidth: 200 }}
                                value={data.preferredAuth || 'None'}
                                helperText="Sign-in method suggested to participants"
                                onChange={e => setData({ ...data, preferredAuth: e.target.value })}
                            >
                                <MenuItem key="none" value="None">None</MenuItem>
                                {
                                    preferredAuthOptions.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))
                                }
                            </TextField>
                        </Stack>
                    </EditorSection>

                    <EditorSection icon={<GavelIcon />} title="Judging" description="What each team is scored on, 1 to 10 per category. Drag to reorder."
                        help={<HelpLink guide="categories">Choosing categories</HelpLink>}>
                        <CategoryEditor value={{ categories: data.categories, categoryMeta: data.categoryMeta }}
                            onChange={next => setData(d => ({ ...d, ...next }))} />

                        <Stack direction="column" spacing={1.5}>
                            <SettingToggle
                                checked={data.participantScores}
                                label="Participants score too"
                                // organizers skim past this and later tell participants to vote; say what "off" means
                                description={data.participantScores
                                    ? (data.categoryMeta.length > 0 && data.categoryMeta.every(m => m.judgeOnly)
                                        // said in place: otherwise it reads as on while participants get nothing to score
                                        ? "On, but every category is Judges Only, so participants have nothing to score and won't be asked to vote."
                                        : 'On: participants also score the other teams (anonymously, never their own), next to the judges.')
                                    : 'Off: only the judges score. Participants will see that there is nothing for them to vote on.'}
                                onChange={checked => setData({ ...data, participantScores: checked })} />
                            <SettingToggle
                                checked={data.blindJudgeScores}
                                label="Blind judge scores"
                                description="Judges do not see other judges' scores"
                                onChange={checked => setData({ ...data, blindJudgeScores: checked })} />
                            <SettingToggle
                                checked={data.anonymousJudgeScores}
                                label="Anonymous judge scores"
                                description="Scores are not attributed to individual judges"
                                onChange={checked => setData({ ...data, anonymousJudgeScores: checked })} />
                        </Stack>
                    </EditorSection>

                    <Box id="branding" sx={{ scrollMarginTop: 88 }}>
                    <EditorSection icon={<PaletteIcon />} title="Organization & branding" description="Make the hackathon look like your company's. Empty fields use your organization's branding.">
                        {
                            plan === 'pro'
                                ? <Alert severity="success">Pro event: your branding shows to everyone in this hackathon.</Alert>
                                : (
                                    <Alert severity="info" action={upgradeUrl && (
                                        <Button color="inherit" size="small" component={RouterLink} to={upgradeUrl} sx={{ whiteSpace: 'nowrap' }}>
                                            Upgrade{proPrice ? ` · ${proPrice}` : ''}
                                        </Button>
                                    )}>
                                        Custom branding is a <b>Pro event</b> feature{proPrice ? ` (${proPrice} per event)` : ''}. Set it up and preview it here;
                                        participants see it once {plan ? 'you upgrade this event' : 'you upgrade the event from its Organize page'}.
                                    </Alert>
                                )
                        }
                        {
                            organizationOptions.length > 0 && (
                                <TextField
                                    select
                                    label="Organization"
                                    value={data.organization || ''}
                                    sx={{ maxWidth: 360 }}
                                    helperText={selectedOrganization ? `Uses ${selectedOrganization.name}'s branding by default` : 'Not linked to an organization'}
                                    onChange={(e) => {
                                        const org = organizations.find(o => o._id === e.target.value);
                                        // the organization's standard for who can join; the organizer can still change it below
                                        setData({ ...data, organization: e.target.value, ...(applyOrgAccess ? orgAccessPatch(org) : {}) });
                                    }}>
                                    <MenuItem value="">None</MenuItem>
                                    {
                                        organizationOptions.map(org => (
                                            <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            )
                        }
                        <BrandingEditor
                            value={data.branding}
                            onChange={setBranding}
                            inherited={selectedOrganization?.branding}
                            inheritedFrom={selectedOrganization?.name}
                            previewName={previewName} />
                    </EditorSection>
                    </Box>

                    <EditorSection icon={<ArticleIcon />} title="Idea template" description="Optional. Pre-fills the description when someone submits an idea.">
                        <React.Suspense fallback={<div>Loading...</div>}>
                            <Editor toolbar={true} value={data.ideaTemplate} onChange={setIdeaTemplate} />
                        </React.Suspense>
                    </EditorSection>

                    <form onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (!data.name) {
                            setError('Name is required');
                            return;
                        }
                        setError('');

                        onSubmit();
                    }}>
                        <Stack spacing={2}>
                            {(error || submitError) && <Alert severity="error">{error || submitError}</Alert>}
                            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                <Button type="submit" variant='contained' size="large">{submitLabel}</Button>
                                {actions}
                                <Button onClick={() => {
                                    window.history.back();
                                }} size="large">Cancel</Button>
                            </Stack>
                        </Stack>
                    </form>
                </Stack>
            </PageContainer >

        </>
    )
}
